/**
 * Delete account, at the bottom of Settings → Profile (TMMAPP-0012 cooling-off; TMMAPP-0119 item 23
 * and §E 26c). The cooling-off deletion stays the default (Operator, 2026-10-09); the dialog's
 * second choice, Delete now, erases in the request once the person types the account's email
 * (`POST /v1/me/account-deletion {immediate: true, confirmation}`), then signs out. While a
 * cooling-off deletion is pending, the notice keeps Cancel deletion and offers "Delete now instead",
 * the same Delete now confirmation on its own (TMMAPP-0122 item 31). A frozen person is refused
 * Delete now (`409 USER_FROZEN`, TMMAPI-0158): the dialog stays open and says so, and the cooling-off
 * choice still works (item 22).
 */
import { useId, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { accountDeletionApi } from '@/api/accountDeletion';
import { errorCopy } from '@/api/errorCopy';
import { ErrorCodes, isApiError } from '@/api/errors';
import { useAuth } from '@/auth/authHooks';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { formatInstant } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useMe, useMeRefetch } from '@/session/meHooks';
import type { SettingsNotify } from './settingsHref';

interface AccountTabProps {
  onNotify: SettingsNotify;
}

type DeleteChoice = 'coolingOff' | 'now';

export function AccountTab({ onNotify }: AccountTabProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const refetchMe = useMeRefetch();
  const { signOut } = useAuth();
  const zone = me.user.timezone ?? 'UTC';
  const choiceLabelId = useId();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [choice, setChoice] = useState<DeleteChoice>('coolingOff');
  // Opened from the pending notice: Delete now only (the cooling-off deletion is already asked for).
  const [nowOnly, setNowOnly] = useState(false);
  const [email, setEmail] = useState('');
  const [coolingOffUntil, setCoolingOffUntil] = useState<string | null>(
    me.deletionRequested?.coolingOffUntil ?? null,
  );
  const [alreadyRequested, setAlreadyRequested] = useState(false);

  const expected = t('settings.deleteConfirmWord');
  const now = choice === 'now';

  function formatUntil(iso: string): string {
    try {
      return formatInstant(iso, locale, zone);
    } catch {
      return iso;
    }
  }

  async function handleDelete() {
    if (busy || coolingOffUntil !== null) return;
    setBusy(true);
    setDeleteError(null);
    try {
      const result = await accountDeletionApi.request();
      setCoolingOffUntil(result.coolingOffUntil);
      setDeleteOpen(false);
      await refetchMe();
    } catch (error) {
      if (isApiError(error) && error.code === ErrorCodes.DELETION_ALREADY_REQUESTED) {
        setDeleteOpen(false);
        await refetchMe();
        setAlreadyRequested(true);
        setCoolingOffUntil(me.deletionRequested?.coolingOffUntil ?? coolingOffUntil);
        return;
      }
      setDeleteError(t('settings.errorGeneric'));
    } finally {
      setBusy(false);
    }
  }

  async function handleDeleteNow() {
    const confirmation = email.trim();
    if (busy || confirmation === '') return;
    setBusy(true);
    setDeleteError(null);
    try {
      const result = await accountDeletionApi.eraseNow(confirmation);
      // Erased (200), or failed partway and finishing in the job (202): either way the data is
      // going, and this session's person no longer exists.
      await signOut({ deleted: result.erased ? 'erased' : 'erasing' });
    } catch (error) {
      setDeleteError(
        isApiError(error) && error.code === ErrorCodes.USER_FROZEN
          ? // From the pending notice the cooling-off deletion already exists (TMMAPP-0124 item 9).
            t(nowOnly ? 'settings.deleteNowFrozenPending' : 'settings.deleteNowFrozen')
          : errorCopy(t, error, 'settings.errorGeneric'),
      );
      setBusy(false);
    }
  }

  async function handleCancelDeletion() {
    if (busy) return;
    setBusy(true);
    try {
      await accountDeletionApi.cancel();
      setCoolingOffUntil(null);
      await refetchMe();
    } catch {
      onNotify(t('settings.errorGeneric'), 'error');
    } finally {
      setBusy(false);
    }
  }

  const until = coolingOffUntil ?? me.deletionRequested?.coolingOffUntil ?? null;
  const showPending = until !== null || alreadyRequested;

  function openDelete(only: boolean) {
    setDeleteError(null);
    setChoice(only ? 'now' : 'coolingOff');
    setNowOnly(only);
    setEmail('');
    setDeleteOpen(true);
  }

  return (
    <Box>
      {showPending ? (
        <Alert
          severity="warning"
          action={
            // In flight, the label keeps the alert's own `warningText` ink (a disabled button
            // would paint `text3` on `warningSoft`, an unguaranteed pair, TMMAPP-0053): busy is
            // a spinner in that ink plus `aria-busy` / `aria-disabled`, and the handler ignores
            // a second press.
            <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Button
                color="inherit"
                size="small"
                aria-disabled={busy || undefined}
                aria-busy={busy || undefined}
                startIcon={
                  busy ? <CircularProgress size={14} color="inherit" aria-hidden /> : undefined
                }
                onClick={() => void handleCancelDeletion()}
              >
                {t('settings.cancelDeletion')}
              </Button>
              <Button
                color="inherit"
                size="small"
                aria-disabled={busy || undefined}
                onClick={() => {
                  if (!busy) openDelete(true);
                }}
              >
                {t('settings.deleteNowInstead')}
              </Button>
            </Stack>
          }
        >
          {t('settings.deletionPending', { until: until ? formatUntil(until) : '—' })}
        </Alert>
      ) : (
        <Stack spacing={2} sx={{ maxWidth: 560 }}>
          <Typography variant="body2">{t('settings.deleteBody')}</Typography>
          <Box>
            <Button color="error" variant="outlined" onClick={() => openDelete(false)}>
              {t('settings.deleteSubmit')}
            </Button>
          </Box>
        </Stack>
      )}
      {deleteOpen && (
        <ConfirmDialog
          title={t('settings.deleteAccountTitle', { email: me.user.email })}
          consequence={now ? t('settings.deleteNowBody') : t('settings.deleteBody')}
          confirmLabel={now ? t('settings.deleteNowSubmit') : t('settings.deleteSubmit')}
          destructive
          busy={busy}
          error={deleteError}
          requireText={now ? undefined : expected}
          requireTextLabel={now ? undefined : t('settings.deleteConfirmLabel', { word: expected })}
          confirmDisabled={now && email.trim() === ''}
          onConfirm={() => void (now ? handleDeleteNow() : handleDelete())}
          onCancel={() => setDeleteOpen(false)}
        >
          {!nowOnly && (
            <Box>
              <Typography id={choiceLabelId} variant="body2" sx={{ fontWeight: 500 }}>
                {t('settings.deleteChoiceLabel')}
              </Typography>
              <RadioGroup
                aria-labelledby={choiceLabelId}
                value={choice}
                onChange={(event) => {
                  setChoice(event.target.value === 'now' ? 'now' : 'coolingOff');
                  setDeleteError(null);
                }}
              >
                <FormControlLabel
                  value="coolingOff"
                  control={<Radio disabled={busy} />}
                  label={t('settings.deleteChoiceCoolingOff')}
                />
                <FormControlLabel
                  value="now"
                  control={<Radio disabled={busy} />}
                  label={t('settings.deleteChoiceNow')}
                />
              </RadioGroup>
            </Box>
          )}
          {now && (
            <TextField
              label={t('settings.confirmEmailLabel')}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              disabled={busy}
              type="email"
              autoComplete="off"
              fullWidth
            />
          )}
        </ConfirmDialog>
      )}
    </Box>
  );
}
